import { useState } from "react";
import { getPasswordChecks, getPasswordStrength } from "../../utils/passwordRules";

const EyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// Input de contraseña con botón para mostrar/ocultar.
function PasswordInput({ value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...s.input, paddingRight: 44 }}
        autoComplete="new-password"
        required
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        style={s.eyeBtn}
        aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        title={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        tabIndex={-1}
      >
        {show ? EyeOffIcon : EyeIcon}
      </button>
    </div>
  );
}

// Campos de contraseña + confirmación con medidor de fortaleza y checklist
// (5 reglas + coincidencia). Reutilizado por aceptar invitación y reset.
export default function PasswordFields({
  password,
  confirmPassword,
  onChangePassword,
  onChangeConfirm,
  passwordLabel = "Contraseña",
}) {
  const pw = password || "";
  const checks = getPasswordChecks(pw);
  const strength = getPasswordStrength(pw);
  const show = pw.length > 0;
  const match = (confirmPassword || "").length > 0 && confirmPassword === pw;

  return (
    <>
      <div style={s.field}>
        <label style={s.label}>{passwordLabel}</label>
        <PasswordInput value={password} onChange={onChangePassword} />
      </div>

      {show && (
        <div style={s.box}>
          <div style={s.bars}>
            {[1, 2, 3].map((seg) => (
              <div
                key={seg}
                style={{ ...s.bar, background: strength.level >= seg ? strength.color : "#e5e7eb" }}
              />
            ))}
          </div>
          <div style={{ ...s.level, color: strength.color }}>Seguridad: {strength.label}</div>
          <ul style={s.rules}>
            <Rule ok={checks.length}>Al menos 8 caracteres</Rule>
            <Rule ok={checks.upper}>Una mayúscula (A-Z)</Rule>
            <Rule ok={checks.lower}>Una minúscula (a-z)</Rule>
            <Rule ok={checks.number}>Un número (0-9)</Rule>
            <Rule ok={checks.symbol}>Un símbolo (!@#$…)</Rule>
            <Rule ok={match}>Las contraseñas coinciden</Rule>
          </ul>
        </div>
      )}

      <div style={s.field}>
        <label style={s.label}>Confirmar contraseña</label>
        <PasswordInput value={confirmPassword} onChange={onChangeConfirm} />
      </div>
    </>
  );
}

function Rule({ ok, children }) {
  return (
    <li style={{ ...s.rule, color: ok ? "#16a34a" : "#9ca3af" }}>
      <span style={{ fontWeight: 800, width: 14, display: "inline-block" }}>{ok ? "✓" : "○"}</span>
      <span>{children}</span>
    </li>
  );
}

const s = {
  field: { display: "flex", flexDirection: "column", marginBottom: 14 },
  label: { fontSize: 13, fontWeight: 700, color: "#5f6b85", marginBottom: 8 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 14px",
    borderRadius: 14,
    border: "1px solid rgba(109, 94, 252, 0.18)",
    background: "#fbfcff",
    color: "#182033",
    fontSize: 14,
    outline: "none",
  },
  eyeBtn: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    border: "none",
    background: "transparent",
    color: "#7c3aed",
    cursor: "pointer",
    padding: 0,
  },
  box: { margin: "0 0 14px" },
  bars: { display: "flex", gap: 5, marginBottom: 6 },
  bar: { flex: 1, height: 5, borderRadius: 4, transition: "background 0.2s ease" },
  level: { fontSize: 12, fontWeight: 700, marginBottom: 8 },
  rules: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "4px 14px",
  },
  rule: { display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, transition: "color 0.2s ease" },
};
